/* eslint-disable @typescript-eslint/no-explicit-any */
import type { N8NServiceSchema } from "@/types/entities/n8n";
import type { NodeVariable } from "../types";
import { NodeTypes } from "@/workflow_api/types/workflow";
import { getValueType } from "../if/serialization";

const serializer = (
  schema: N8NServiceSchema,
  data: {
    [key: string]: any;
  },
  variables: NodeVariable[],
) => {
  const ids: {
    [key: string]: {
      type: string;
    };
  }[] = schema.sections.flatMap((section) => {
    return Object.entries(section.inputs).map(([key, value]) => ({
      [key]: {
        type: value.type,
      },
    }));
  });

  const inputs: {
    name: string;
    value: string | number | boolean | null | Array<string>;
    field?: string;
    output_name?: string;
    type: string;
    connection?: string;
  }[] = [];

  ids.forEach((id) => {
    const key = Object.keys(id)[0];
    if (data[key]) {
      if (id[key].type === "date-time-range") {
        const input = parseDateTimeRange(data[key], variables);
        if (!input) return;
        inputs.push({
          name: key,
          ...input,
        });
      }
      const type = id[key].type;
      if (type === "date-range") {
        const input = parseDateRange(data[key], variables);
        if (!input) return;
        inputs.push({
          name: key,
          type: "STRING",
          ...input,
        });
      }
      if (type === "date-time") {
        const input = parseDateTime(data[key], variables);
        if (!input) return;
        inputs.push({
          name: key,
          type: "STRING",
          ...input,
        });
      }
      if (
        type === "email" ||
        type === "id" ||
        type === "text" ||
        type === "date"
      ) {
        const input = simpleParse(data[key], variables);
        if (!input) return;
        inputs.push({
          name: key,
          type: "STRING",
          ...input,
        });
      }
      if (type === "number") {
        const input = simpleParse(data[key], variables);
        if (!input) return;
        inputs.push({
          name: key,
          type: "NUMBER",
          ...input,
        });
      }
      if (type === "boolean") {
        const input = parseBoolean(data[key], variables);
        if (!input) return;
        inputs.push({
          name: key,
          type: "BOOLEAN",
          ...input,
        });
      }
      if (type === "array" || type === "select") {
        const input = parseArray(data[key], variables);
        if (!input) return;
        inputs.push({
          name: key,
          type: "ARRAY",
          ...input,
        });
      }
      if (type === "file") {
        const input = parseFile(data[key], variables);
        if (!input) return;
        inputs.push({
          name: key,
          type: "STRING",
          ...input,
        });
      }
    }
  });

  return inputs;
};

export default serializer;

const parseDateTimeRange = (
  data: {
    type: string;
    start: string;
    start_time: string;
    end: string;
    end_time: string;
    value?: string;
    node_id?: string;
    value_type?: string;
  },
  variables: NodeVariable[],
) => {
  if (!data.value && !data.start && !data.end) {
    return {
      value: "",
      type: "STRING",
    };
  }

  if (data.type === "input") {
    return {
      value: `${data.start} ${data.start_time},${data.end} ${data.end_time}`,
      type: "STRING",
    };
  }

  if (data.type === "variable") {
    const val = getValueFromVariable(
      data.value || "",
      data.node_id || "",
      variables,
    );
    if (!val) return;
    return {
      type: "STRING",
      ...val,
    };
  }

  return null;
};

const parseDateRange = (
  data: {
    type: string;
    start?: string;
    end?: string;
    value?: string;
    node_id?: string;
    value_type?: string;
  },
  variables: NodeVariable[],
) => {
  if (!data.value && !data.start && !data.end) {
    return {
      value: "",
    };
  }
  if (data.type === "input") {
    return {
      value: `${data.start},${data.end}`,
    };
  } else {
    return getValueFromVariable(
      data.value || "",
      data.node_id || "",
      variables,
    );
  }
};

const parseDateTime = (
  data: {
    type: string;
    date?: string;
    time?: string;
    value?: string;
    node_id?: string;
    value_type?: string;
  },
  variables: NodeVariable[],
) => {
  if (!data.value && !data.date && !data.time) {
    return {
      value: "",
    };
  }
  if (data.type === "input") {
    return {
      value: `${data.date} ${data.time}`,
    };
  } else {
    return getValueFromVariable(
      data.value || "",
      data.node_id || "",
      variables,
    );
  }
};

const simpleParse = (
  data: {
    type: string;
    value?: string;
    node_id?: string;
    value_type?: string;
  },
  variables: NodeVariable[],
) => {
  if (!data.value)
    return {
      value: "",
    };
  if (data.type === "input") {
    return {
      value: data.value,
    };
  } else {
    console.log(data, "here");
    return getValueFromVariable(data.value, data.node_id || "", variables);
  }
};

const parseBoolean = (
  data: {
    type: string;
    value?: string;
    node_id?: string;
    value_type?: string;
  },
  variables: NodeVariable[],
) => {
  if (data.value === undefined || data.value === null) {
    return {
      value: null,
    };
  }
  if (data.type === "input") {
    return {
      value: data.value === "true",
    };
  } else {
    return getValueFromVariable(
      data.value || "",
      data.node_id || "",
      variables,
    );
  }
};

const parseArray = (
  data: { value: string; node_id: string },
  variables: NodeVariable[],
) => {
  if (!data.value || data.value.length === 0) {
    return {
      value: "",
    };
  }
  return getValueFromVariable(data.value, data.node_id, variables);
};
const parseFile = (
  data: {
    type: string;
    value?: string;
    node_id?: string;
    value_type?: string;
  },
  variables: NodeVariable[],
) => {
  if (!data.value)
    return {
      value: "",
    };
  return getValueFromVariable(data.value, data.node_id || "", variables);
};

const getValueFromVariable = (
  value: string,
  node_id: string,
  varaibles: NodeVariable[],
) => {
  if (!varaibles) return null;

  // if is the same, the value comes from a node that returns a single value
  if (value === node_id) {
    const variable = varaibles.find((nv) => nv.nodeId === node_id);
    if (!variable) return null;
    return {
      value: null,
      connection: variable.nodeId,
    };
  }

  const node = varaibles.find((nv) => nv.nodeId === node_id);

  const variable = node?.variables.find((v) => v.id === value);
  console.log(variable, node?.variables);

  if (node?.nodeId === "global-variables") {
    return {
      global_type: variable?.global_type,
      value: variable?.id as string,
      type: getValueType(variable?.type || "string"),
    };
  }

  if (node?.ref_name === NodeTypes.FORM) {
    return {
      value: null,
      connection: node.nodeId,

      ...(variable?.output_name
        ? {
            output_name: variable.output_name,
            field: variable.field_id,
          }
        : {
            field: variable?.id,
          }),
    };
  }

  if (
    node?.type === NodeTypes.INTEROPERABILITY ||
    node?.type === NodeTypes.N8N
  ) {
    return {
      value: null,
      connection: node.nodeId,
      output_name: variable?.output_name,
    };
  }

  if (node?.type === NodeTypes.EXPEDIENTE) {
    return {
      value: null,
      connection: node.nodeId,
      output_name: variable?.output_name,
    };
  }

  return null;
};
